import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCheckSquare,
  faExclamationTriangle,
  faInfoCircle,
  faXmarkSquare,
} from "@fortawesome/pro-duotone-svg-icons";
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from "@fortawesome/react-fontawesome";
import { VariantProps, tv } from "tailwind-variants";

const styles = tv({
  slots: {
    base: "flex flex-col gap-10 overflow-hidden rounded-sm p-10 md:p-20",
    titleSlotContainer: "flex w-full flex-row items-center gap-20",
    childrenContainer: "md:pl-40",
    iconStyles: "h-20 w-20",
  },
  variants: {
    variant: {
      success: {
        base: "bg-green/20",
        iconStyles: "text-green",
      },
      info: { base: "bg-primary-light/50", iconStyles: "text-primary" },
      error: {
        base: "bg-red/30",
        iconStyles: "text-red",
      },
      warning: {
        base: "bg-orange/25",
        iconStyles: "text-orange",
      },
    },
    rounded: {
      sm: {
        base: "rounded-sm",
      },
      unset: {
        base: "rounded-[unset]",
      },
    },
  },
  defaultVariants: {
    variant: "info",
  },
});
interface IProps {
  icon?: IconProp | string;
  iconClassName?: string;
  variant?: VariantProps<typeof styles>["variant"];
  children?: React.ReactNode;
  titleSlot?: React.ReactNode;
  iconProps?: Omit<FontAwesomeIconProps, "icon">;
  rounded?: VariantProps<typeof styles>["rounded"];
  testId?: string;
}

const Alert = ({
  icon,
  iconClassName,
  variant,
  titleSlot,
  children,
  iconProps,
  rounded = "sm",
  testId,
}: IProps) => {
  const { base, titleSlotContainer, iconStyles, childrenContainer } = styles({
    variant,
    rounded,
  });

  const getIcon = () => {
    switch (variant) {
      case "warning":
        return faExclamationTriangle;
      case "success":
        return faCheckSquare;
      case "error":
        return faXmarkSquare;
      default:
        return faInfoCircle;
    }
  };
  const renderIcon = () => {
    const leadingIcon = getIcon();

    if (typeof icon === "string")
      return <img src={icon} className={iconClassName} alt="Alert icon" />;

    return (
      <FontAwesomeIcon
        icon={icon || leadingIcon}
        className={iconStyles({ className: iconClassName })}
        {...iconProps}
      />
    );
  };

  return (
    <div className={base()} role="alert" data-testid={testId}>
      {!!titleSlot && (
        <div className={titleSlotContainer()}>
          {renderIcon()} {titleSlot}
        </div>
      )}
      {!!children && <div className={childrenContainer()}>{children}</div>}
    </div>
  );
};

export default Alert;
